<script setup lang="ts">
import { computed } from 'vue';
import { useMainStore } from '../../store';
import { trackQuizQuestion, trackQuizAnswerSkip } from '../../analytics/events';
import type { Answer, FlowScreen } from '../../types';
import type { TemplateDefinition } from '../../templates';

// Core question block (DEV-1135): owns answer recording + analytics + the
// skip path — identical event semantics to legacy Question.vue (one canonical
// event per user action; skip answers fire ONLY quiz_answer_skip). The
// template's question component is presentation only.
const props = defineProps<{
  screen: Extract<FlowScreen, { type: 'question' }>;
  template: TemplateDefinition;
}>();
const emit = defineEmits<{ advance: [] }>();

const store = useMainStore();

const question = computed(() =>
  store.quizData.questions?.find((q) => q.id === props.screen.question_id)
);

const selectedValues = computed(() =>
  store.questionAnswers
    .filter(([qId]) => qId === props.screen.question_id)
    .map(([, value]) => value)
);

// 0-based ordinal among question screens, mirroring legacy currentIndex
// semantics for the question_index event field.
const questionOrdinal = computed(() => store.flowQuestionProgress.current - 1);

const handleAnswer = (answer: Answer) => {
  const q = question.value;
  if (!q) return;
  const isSkip = answer[4] === true;
  const isDuplicate = store.questionAnswers.some(
    ([qId, a]) => qId === q.id && a === answer[0]
  );

  if (!isDuplicate) {
    const payload = {
      quiz_id: store.quizData.quiz_id,
      question_index: questionOrdinal.value,
      question_text: q.title,
      answer_text: String(answer[1]),
    };
    if (isSkip) {
      trackQuizAnswerSkip(payload);
    } else {
      trackQuizQuestion(payload);
    }
  }

  // Recording is decoupled from advancement; re-answers (back-nav) replace.
  store.recordFlowAnswer([q.id, answer[0]]);

  if (isSkip) {
    store.redirectToLanding();
    return;
  }

  emit('advance');
};
</script>

<template>
  <component
    :is="template.question"
    v-if="question"
    :question="question"
    :selected="selectedValues"
    @answer="handleAnswer"
  />
</template>
